import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  Box,
  Dialog,
  DialogContentText,
  DialogTitle,
  Stack,
  SxProps,
  TextField,
  Theme,
  alpha,
} from '@mui/material';

import AppButton, { AppButtonProps } from '@/shared/ui/AppButton';

type ReasonVariant = 'reject' | 'delay';

interface ReasonVariantConfig {
  testId: string;
  ariaId: string;
  icon: ReactNode;
  circleBg: string | ((theme: Theme) => string);
  titleKey: string;
  descriptionKey: string;
  placeholderKey: string;
  inputBg: (theme: Theme) => string;
  inputBorderColor: (theme: Theme) => string;
  confirmVariant: AppButtonProps['variant'];
  confirmLabelKey: string;
  confirmSx?: SxProps<Theme>;
}

const VARIANTS: Record<ReasonVariant, ReasonVariantConfig> = {
  reject: {
    testId: 'reject-task-dialog',
    ariaId: 'rejection-reason-dialog',
    icon: <CancelOutlinedIcon sx={{ fontSize: 40, color: '#e9554a' }} />,
    circleBg: '#fde5e3',
    titleKey: 'reject_task',
    descriptionKey: 'reject_task_reason_description',
    placeholderKey: 'rejection_reason_placeholder',
    inputBg: () => '#fef3f2',
    inputBorderColor: (theme) => theme.palette.red[500],
    confirmVariant: 'p-alert',
    confirmLabelKey: 'Reject',
  },
  delay: {
    testId: 'delay-task-dialog',
    ariaId: 'delay-reason-dialog',
    icon: (
      <AccessTimeOutlinedIcon sx={{ fontSize: 40, color: (theme) => theme.palette.orange[600] }} />
    ),
    circleBg: (theme: Theme) => theme.palette.orange[100],
    titleKey: 'delay_task',
    descriptionKey: 'delay_task_reason_description',
    placeholderKey: 'delay_reason_placeholder',
    inputBg: (theme) => theme.palette.orange[50],
    inputBorderColor: (theme) => theme.palette.orange[500],
    confirmVariant: 'primary',
    confirmLabelKey: 'Confirm delay',
    confirmSx: {
      backgroundColor: (theme: Theme) => theme.palette.orange[500],
      '&:hover': { backgroundColor: (theme: Theme) => theme.palette.orange[600] },
      '&.Mui-disabled': {
        backgroundColor: (theme: Theme) => alpha(theme.palette.orange[500], 0.35),
        color: alpha('#fff', 0.5),
      },
    },
  },
};

interface ReasonDialogProps {
  open: boolean;
  variant: ReasonVariant;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

const ReasonDialog: FC<ReasonDialogProps> = ({ open, variant, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const config = VARIANTS[variant];

  const handleConfirm = () => {
    onConfirm(reason);
    setReason('');
  };

  const handleCancel = () => {
    setReason('');
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby={config.ariaId}
      PaperProps={{
        'data-testid': config.testId,
        sx: {
          borderRadius: '24px',
          padding: '32px',
          width: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
        },
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: config.circleBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {config.icon}
      </Box>

      <Stack alignItems="center" gap="12px" width="100%">
        <DialogTitle
          id={config.ariaId}
          sx={{ p: 0, fontSize: '22px', fontWeight: 500, color: '#010203', textAlign: 'center' }}
        >
          {t(config.titleKey)}
        </DialogTitle>
        <DialogContentText sx={{ fontSize: '12px', color: '#615e6f', textAlign: 'center', m: 0 }}>
          {t(config.descriptionKey)}
        </DialogContentText>
      </Stack>

      <TextField
        autoFocus
        fullWidth
        multiline
        rows={4}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t(config.placeholderKey)}
        inputProps={{ 'data-testid': 'reason-dialog-input' }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '0.75rem',
            backgroundColor: config.inputBg,
            border: (theme) => `0.5px solid ${config.inputBorderColor(theme)}`,
          },
        }}
      />

      <Stack direction="row" gap="1rem" width="100%">
        <AppButton
          variant="s-alert"
          fullWidth
          onClick={handleCancel}
          data-testid="reason-dialog-cancel"
        >
          {t('Cancel')}
        </AppButton>
        <AppButton
          variant={config.confirmVariant}
          fullWidth
          onClick={handleConfirm}
          disabled={!reason.trim()}
          data-testid="reason-dialog-confirm"
          sx={config.confirmSx}
        >
          {t(config.confirmLabelKey)}
        </AppButton>
      </Stack>
    </Dialog>
  );
};

export default ReasonDialog;
